<template>
  <div class="login-page">
    <div class="grid-shell">
      <!-- Left -->
      <section class="left">
        <img class="logo" src="@/assets/logo.png" alt="LYON logo" />
        <div class="greeting">
          <h1>Hi,</h1>
          <h1>Welcome Back</h1>
        </div>

        <form class="form" @submit.prevent="handleSignIn">
          <!-- Username -->
          <label class="field">
            <span class="label">User Name</span>
            <input
              v-model.trim="userName"
              type="email"
              placeholder="Enter your email"
              autocomplete="username"
              required
            />
          </label>

          <!-- Password -->
          <label class="field">
            <span class="label">Password</span>
            <div class="password-wrap">
              <input
                :type="showPass ? 'text' : 'password'"
                v-model="password"
                placeholder="Enter password"
                autocomplete="current-password"
                required
              />
              <button
                type="button"
                class="eye"
                :aria-pressed="showPass ? 'true' : 'false'"
                @click="showPass = !showPass"
                title="Toggle password visibility"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path v-if="!showPass" fill="currentColor"
                        d="M12 4.5c5.5 0 9.7 4.4 10.7 6-1 1.6-5.2 6-10.7 6S2.3 12.1 1.3 10.5c1-1.6 5.2-6 10.7-6zm0 2c-3.9 0-7.3 2.7-8.8 4.1 1.5 1.4 4.9 4.1 8.8 4.1s7.3-2.7 8.8-4.1C19.3 9.2 15.9 6.5 12 6.5zm0 1.8a3.7 3.7 0 1 1 0 7.4 3.7 3.7 0 0 1 0-7.4z"/>
                  <path v-else fill="currentColor"
                        d="M3.3 2.9 2 4.2l4 4C3.8 9.3 2.4 10.9 1.3 12.5c1 1.6 5.2 6 10.7 6 2.2 0 4.1-.5 5.7-1.2l4.3 4.3 1.3-1.3L3.3 2.9zM8.8 9l1.3 1.3a3.7 3.7 0 0 0 4.6 4.6l1.3 1.3c-.9.4-2 .6-3 .6-3.9 0-7.3-2.7-8.8-4.1.7-.7 1.7-1.6 3-2.4L8.8 9zm3.2-.9c1.7 0 3.2 1.4 3.2 3.2 0 .3 0 .6-.1.9l1.6 1.6c.3-.7.5-1.6.5-2.5a5.2 5.2 0 0 0-5.2-5.2c-.9 0-1.7.2-2.5.5l1.6 1.6c.3-.1.6-.1.9-.1z"/>
                </svg>
              </button>
            </div>
          </label>

          <!-- Under fields -->
          <div class="form-bottom">
            <button type="button" class="link" @click="openResetModal">Forgot password?</button>
          </div>

          <!-- Invalid slug warning -->
          <div v-if="!companyId" class="slug-error">
            Invalid login link. Please use your company's login URL.
          </div>

          <!-- Submit -->
          <button class="primary mt-6" type="submit" :disabled="signingIn || !companyId">
            <span v-if="!signingIn">Sign in</span>
            <span v-else>Signing in…</span>
          </button>
        </form>
      </section>

      <!-- Right -->
      <section class="right">
        <div class="art-card">
          <img src="@/assets/Login.png" alt="Login Illustration" />
        </div>
      </section>
    </div>

    <!-- Backdrop -->
    <transition name="fade">
      <div
        v-if="anyModalOpen"
        class="auth-backdrop"
        @click.self="closeAllModals"
      />
    </transition>

    <!-- Modal: Reset Email -->
    <transition name="pop">
      <div
        v-if="showReset"
        class="auth-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-title"
      >
        <button class="x" @click="closeAllModals" aria-label="Close">✕</button>
        <h3 id="reset-title" class="title">Reset Password</h3>
        <p class="subtitle">
          Enter your email and we will send you a verification code to reset your password.
        </p>

        <label class="field center">
          <span class="label">Email</span>
          <input
            ref="emailInput"
            v-model.trim="resetEmail"
            type="email"
            placeholder="Enter Email"
            autocomplete="email"
            required
          />
        </label>

        <button class="primary center" @click="handleSendOtp" :disabled="!resetEmail || sendingOtp">
          <span v-if="!sendingOtp">Continue</span>
          <span v-else>Sending…</span>
        </button>
      </div>
    </transition>

    <!-- Modal: OTP -->
    <transition name="pop">
      <div
        v-if="showOtp"
        class="auth-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="otp-title"
      >
        <button class="x" @click="closeAllModals" aria-label="Close">✕</button>
        <h3 id="otp-title" class="title">Enter your verification code</h3>
        <p class="subtitle">We've sent a verification code to <br> your Email</p>

        <div class="otp" role="group" aria-label="6-digit code">
          <input
            v-for="i in 6"
            :key="i"
            :ref="el => setOtpRef(i - 1, el)"
            maxlength="1"
            inputmode="numeric"
            pattern="[0-9]*"
            @input="onOtpInput(i - 1, $event)"
            @keydown.backspace.prevent="onOtpBackspace(i - 1, $event)"
            @paste.prevent="onOtpPaste($event)"
            aria-label="Digit"
          />
        </div>

        <button class="primary center" @click="goSetNew" :disabled="otpValue.length !== 6">Continue</button>
        <button class="ghost center" type="button" @click="resendCode" :disabled="resending">
          {{ resending ? 'Resending…' : 'Resend code' }}
        </button>
      </div>
    </transition>

    <!-- Modal: Set New Password -->
    <transition name="pop">
      <div
        v-if="showSetNew"
        class="auth-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="newpass-title"
      >
        <button class="x" @click="closeAllModals" aria-label="Close">✕</button>
        <h3 id="newpass-title" class="title">Set your password</h3>
        <p class="subtitle">Please create your account password.</p>

        <label class="field center">
          <span class="label">Password</span>
          <div class="password-wrap">
            <input
              ref="newPassInput"
              :type="showNewPass ? 'text' : 'password'"
              v-model="newPass"
              placeholder="Enter password"
              required
            />
            <button
              type="button"
              class="eye"
              @click="showNewPass = !showNewPass"
              :aria-pressed="showNewPass ? 'true' : 'false'"
              title="Toggle password visibility"
            >
              <svg v-if="!showNewPass" xmlns="http://www.w3.org/2000/svg" fill="none"
                   viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943
                         9.542 7-1.274 4.057-5.065 7-9.542 7-4.477
                         0-8.268-2.943-9.542-7z"/>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none"
                   viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477
                         0-8.268-2.943-9.542-7a10.06 10.06 0 012.338-4.044m3.793-2.32
                         A9.956 9.956 0 0112 5c4.477 0 8.268 2.943
                         9.542 7a10.056 10.056 0 01-4.132 5.411M15
                         12a3 3 0 01-4.243 2.828M9.88 9.88A3 3 0
                         0115 12m-6 0a3 3 0 010-6"/>
              </svg>
            </button>
          </div>
        </label>

        <label class="field center">
          <span class="label">Confirm Password</span>
          <input :type="showNewPass ? 'text' : 'password'" v-model="newPassConfirm" placeholder="Enter confirm password" required />
        </label>

        <button class="primary center" @click="handleResetPassword" :disabled="!canReset">Reset Password</button>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/api.js'
import Swal from 'sweetalert2'

// ── helper: toast shortcut ──────────────────────────────────────────
const toast = (icon, title, text = '') => Swal.fire({
  icon,
  title,
  text: text || undefined,
  toast: true,
  position: 'top',
  timer: icon === 'success' ? 1400 : 2200,
  showConfirmButton: false,
})

export default {
  name: 'Login',
  setup () {
    const route = useRoute()

    const companyId = computed(() => route.params.slug || '')

    // ------ form
    const userName  = ref('')
    const password  = ref('')
    const showPass  = ref(false)
    const signingIn = ref(false)

    // ------ modals
    const showReset    = ref(false)
    const showOtp      = ref(false)
    const showSetNew   = ref(false)
    const anyModalOpen = computed(() => showReset.value || showOtp.value || showSetNew.value)

    // ------ reset
    const resetEmail = ref('')
    const sendingOtp = ref(false)
    const emailInput = ref(null)

    // ------ otp
    const otpInputs = ref(Array(6).fill(null))
    const setOtpRef = (idx, el) => (otpInputs.value[idx] = el)
    const otpValue  = ref('')
    const resending = ref(false)

    // ------ new pass
    const newPass        = ref('')
    const newPassConfirm = ref('')
    const showNewPass    = ref(false)
    const newPassInput   = ref(null)
    const canReset = computed(() => newPass.value.length >= 6 && newPass.value === newPassConfirm.value)

    const closeAllModals = () => {
      showReset.value = showOtp.value = showSetNew.value = false
    }

    const openResetModal = async () => {
      resetEmail.value = ''
      showReset.value  = true
      showOtp.value    = false
      showSetNew.value = false
      await nextTick()
      emailInput.value?.focus()
    }

    // ------ Sign in
    const handleSignIn = async () => {
      if (!userName.value || !password.value) return
      if (!companyId.value) {
        toast('error', 'Invalid login link', "Please use your company's login URL.")
        return
      }

      signingIn.value = true
      try {
        const res = await api.post('/accounting/login', {
          company_id: companyId.value,
          email:      userName.value,
          password:   password.value,
        })

        const token = res?.data?.token
        if (!token) throw new Error(res?.data?.message || 'Login failed')

        localStorage.setItem('token',      token)
        localStorage.setItem('user',       JSON.stringify(res?.data?.user || {}))
        localStorage.setItem('company_id', companyId.value)
        localStorage.setItem('lastActivity', Date.now())
        window.location.href = '/dashboard'
      } catch (e) {
        toast('error', 'Sign in failed', e?.response?.data?.message || e?.message || 'Error while signing in')
      } finally {
        signingIn.value = false
      }
    }

    // ------ Reset → OTP → New
    const handleSendOtp = async () => {
      if (!resetEmail.value) return
      sendingOtp.value = true
      try {
        await api.post('/accounting/password/forgot', {
          email:      resetEmail.value,
          company_id: companyId.value,
        })
        showReset.value = false
        showOtp.value   = true
        await nextTick()
        focusOtp(0)
      } catch (err) {
        toast('error', 'Failed to send code', err?.response?.data?.message || 'Please try again.')
      } finally {
        sendingOtp.value = false
      }
    }

    const resendCode = async () => {
      resending.value = true
      try {
        await api.post('/accounting/password/forgot', {
          email:      resetEmail.value,
          company_id: companyId.value,
        })
        toast('success', 'Code resent successfully')
      } catch (err) {
        toast('error', 'Failed to resend code', err?.response?.data?.message || 'Please try again.')
      } finally {
        resending.value = false
      }
    }

    const rebuildOtp = () => {
      otpValue.value = otpInputs.value
        .map(el => (el && el.value ? el.value.replace(/\D/g, '').slice(0, 1) : ''))
        .join('')
    }

    const focusOtp = (idx) => {
      const el = otpInputs.value[idx]
      if (el) el.focus()
    }

    const onOtpInput = (i, evt) => {
      const v = (evt.target.value || '').replace(/\D/g, '').slice(0, 1)
      evt.target.value = v
      rebuildOtp()
      if (v && i < 5) focusOtp(i + 1)
    }

    const onOtpBackspace = (i, evt) => {
      if (!evt.target.value && i > 0) focusOtp(i - 1)
      else {
        evt.target.value = ''
        rebuildOtp()
      }
    }

    const onOtpPaste = (evt) => {
      const text = (evt.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '').slice(0, 6)
      if (!text) return
      otpInputs.value.forEach((el, idx) => { if (el) el.value = text[idx] || '' })
      rebuildOtp()
      focusOtp(Math.min(text.length - 1, 5))
    }

    const goSetNew = async () => {
      if (otpValue.value.length !== 6) {
        toast('warning', 'Please enter a 6-digit code')
        return
      }
      try {
        await api.post('/accounting/password/verify-otp', {
          email:      resetEmail.value,
          company_id: companyId.value,
          otp:        otpValue.value,
        })
        showOtp.value    = false
        showSetNew.value = true
        await nextTick()
        newPassInput.value?.focus()
      } catch (err) {
        toast('error', 'Invalid code', err?.response?.data?.message || 'Please try again.')
      }
    }

    const handleResetPassword = async () => {
      if (!canReset.value) return
      try {
        await api.post('/accounting/password/reset', {
          email:                 resetEmail.value,
          company_id:            companyId.value,
          otp:                   otpValue.value,
          password:              newPass.value,
          password_confirmation: newPassConfirm.value,
        })
        await toast('success', 'Password updated successfully')
        closeAllModals()
      } catch (err) {
        toast('error', 'Failed to reset password', err?.response?.data?.message || 'Please try again.')
      }
    }

    onMounted(() => {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeAllModals()
      })
    })

    return {
      companyId,
      userName, password, showPass, signingIn, handleSignIn,
      showReset, showOtp, showSetNew, anyModalOpen, openResetModal, closeAllModals,
      resetEmail, sendingOtp, handleSendOtp, emailInput,
      otpValue, resending, resendCode, setOtpRef, onOtpInput, onOtpBackspace, onOtpPaste, goSetNew,
      newPass, newPassConfirm, showNewPass, canReset, handleResetPassword, newPassInput,
    }
  }
}
</script>

<style scoped>
/* ---------- PAGE LAYOUT ---------- */
.login-page {
  min-height: 100vh;
  background: #ffffff;
  display: grid;
  place-items: center;
  padding: 24px;
}
.grid-shell {
  width: 100%;
  max-width: 1120px;
  display: grid;
  grid-template-columns: 1fr 0.75fr;
  gap: 36px;
  align-items: start;
}

/* ---------- LEFT ---------- */
.left { padding-top: 22px; }
.logo {
  display: block;
  width: 120px;
  margin: 0 auto 38px;
  user-select: none;
}
.greeting h1 {
  margin: 0;
  color: #111827;
  line-height: 1.1;
  font-size: 36px;
  font-weight: 700;
}
.greeting h1 + h1 { margin-top: 4px; }

.form { margin-top: 22px; max-width: 420px; }
.field { display: block; margin-bottom: 16px; }
.label { display: block; font-size: 16px; font-weight: 600; color: #000; margin-bottom: 6px; }
.field input {
  width: 100%;
  height: 40px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0 12px;
  outline: none;
  font-size: 14px;
  transition: border-color .2s, box-shadow .2s;
  background: #fff;
}
.field input::placeholder { color: #b9c1cc; }
.field input:focus { border-color: #b7c7d6; box-shadow: 0 0 0 3px rgba(59,130,246,.08); }

/* password eye */
.password-wrap { position: relative; }
.password-wrap input { padding-right: 36px; }
.eye {
  position: absolute;
  top: 50%; right: 8px;
  transform: translateY(-50%);
  width: 28px; height: 28px;
  border: 0; background: transparent;
  color: #6b7280; cursor: pointer; border-radius: 6px;
}
.eye:hover { background: #f3f4f6; }

/* bottom row */
.form-bottom { display: flex; justify-content: flex-start; margin-top: 4px; margin-bottom: 10px; }
.link { font-size: 12px; color: #3b82f6; background: transparent; border: 0; padding: 0; cursor: pointer; }

/* slug error */
.slug-error {
  font-size: 13px;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 12px;
}

.primary {
  width: 100%; height: 40px; border-radius: 999px;
  background: #5b9ac5; color: #fff; border: 0;
  display: inline-flex; align-items: center; justify-content: center;
  font-weight: 600; transition: filter .2s, transform .02s;
}
.primary:hover { filter: brightness(0.96); }
.primary:disabled { opacity: .6; cursor: not-allowed; }

.ghost {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 20px;
  background: transparent;
  color: #000;
  margin: 8px auto 0;
  width: fit-content;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
}
.ghost:disabled { opacity: .6; }

/* ---------- RIGHT ---------- */
.right img { display: block; width: 100%; height: auto; border-radius: 14px; }

/* ---------- BACKDROP & MODAL ---------- */
.auth-backdrop {
  position: fixed; inset: 0; z-index: 40; background: rgba(17,24,39,0.55);
}
.auth-modal {
  position: fixed; z-index: 50; left: 50%; top: 50%;
  width: 443px; max-width: calc(100vw - 32px);
  background: #fff; border-radius: 16px;
  padding: 40px 60px 28px;
  box-shadow: 0 12px 40px rgba(0,0,0,.18);
  transform: translate(-50%, -50%);
}
.auth-modal .x {
  position: absolute; top: 12px; right: 12px;
  width: 28px; height: 28px; border-radius: 50%;
  border: 1px solid #e5e7eb; background: #fff; color: #111827; cursor: pointer;
}
.auth-modal .x:hover { background: #f9fafb; }
.auth-modal .title {
  text-align: center; font-size: 24px; font-weight: 700; color: #111827;
  margin-top: 8px; margin-bottom: 15px;
}
.auth-modal .subtitle {
  text-align: center; color: #000; font-size: 15px; max-width: 420px; margin: 0 auto 14px;
}
.field.center { margin-bottom: 16px; }
.primary.center { margin: 0 auto;  min-width: 160px; padding: 0 32px; }

/* OTP */
.otp { display: flex; justify-content: center; gap: 10px; margin: 6px 0 32px; }
.otp input {
  width: 42px; height: 42px; border-radius: 8px; border: 1px solid #e5e7eb;
  text-align: center; font-size: 16px; outline: none;
}
.otp input:focus { border-color: #b7c7d6; box-shadow: 0 0 0 3px rgba(59,130,246,.08); }

/* ---------- TRANSITIONS ---------- */
.fade-enter-active, .fade-leave-active { transition: opacity .18s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.pop-enter-active, .pop-leave-active { transition: opacity .18s ease, transform .18s ease; }
.pop-enter-from, .pop-leave-to { opacity: 0; transform: translate(-50%, -48%) scale(.98); }

/* ---------- RESPONSIVE ---------- */
@media (max-width: 1024px) { .grid-shell { grid-template-columns: 1fr .9fr; gap: 28px; } }
@media (max-width: 860px)  {
  .grid-shell { grid-template-columns: 1fr; max-width: 560px; }
  .right { display: none; }
  .greeting h1 { font-size: 32px; }
}
</style>
